import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { getZonasUsuario, actualizarZonaUsuario } from "../../../../service/AffariService";
import LoaderTransparent from "../../../../components/ui/LoaderTransparent";


const ZonasDialog = ({ visible, onCancel, usuario, toast }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    useEffect(() => {
        if (visible && usuario?.id != null) {
            const zonasUsuario = Array.isArray(usuario?.zonas)
                ? usuario.zonas
                : [];
            const zonasUsuarioCodigos = new Set(
                zonasUsuario
                    .map((z) => (typeof z === "object" ? z.codigo : z))
                    .filter((c) => c != null)
            );

            setLoading(true);
            setData([]);
            getZonasUsuario(usuario.id)
                .then((response) => {
                    const zonasResponse = Array.isArray(response?.zonas) ? response.zonas : [];
                    const merged = zonasResponse.map((z) => ({
                        ...z,
                        asignado: zonasUsuarioCodigos.has(z.codigo) || !!z.asignado,
                    }));
                    setData(merged);
                })
                .catch((error) => {
                    console.error("Error fetching zonas data:", error);
                })
                .finally(() => setLoading(false));
        }
    }, [visible, usuario]);

    const handleToggleZona = async (zona, checked) => {
        if (!usuario?.id || !zona?.codigo) return;

        const accion = checked ? "add" : "remove";

        try {
            setLoading(true);
            setData((prev) =>
                Array.isArray(prev)
                    ? prev.map((z) =>
                        z.codigo === zona.codigo ? { ...z, asignado: checked } : z
                    )
                    : prev
            );
            await actualizarZonaUsuario(usuario.id, accion, zona.codigo);

            if (toast?.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Éxito",
                    detail: checked
                        ? "Zona asignada correctamente"
                        : "Zona desasignada correctamente",
                });
            }
        } catch (error) {
            console.error("Error actualizando zona del usuario:", error);
            if (toast?.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: "No se pudo actualizar la zona del usuario",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const header = (
        <div className="flex justify-content-end">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Buscar por código o descripción" />
            </span>
        </div>
    );

    return (
        <Dialog
            header={`Zonas del usuario${usuario?.nombre ? `: ${usuario.nombre}` : ''}`}
            visible={visible}
            className="p-fluid custom-dialog"
            modal
            onHide={onCancel}
            dismissableMask={true}
        >
            <>
                {loading && <LoaderTransparent coverParent overlayTint="transparent" />}
                <DataTable 
                    value={data} 
                    emptyMessage="No se encontraron zonas." 
                    showGridlines 
                    stripedRows 
                    size="small"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    className="p-datatable-gridlines "
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} zonas"
                    globalFilter={globalFilter}
                    header={header}
                    globalFilterFields={['codigo', 'descripcion']}
                    footer={
                        <div className="flex justify-content-center pt-3">
                            <div className="col-2 flex justify-content-center">
                                <Button
                                    label="Cerrar"
                                    icon="pi pi-times"
                                    onClick={onCancel}
                                    className="p-btn-primary"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    }
                >
                    <Column
                        header="#"
                        body={(row, options) => options.rowIndex + 1}
                        style={{ width: '3rem' }}
                    />
                    <Column
                        header="Usar"
                        body={(row) => (
                            <div className="flex justify-content-center">
                                <Checkbox
                                    checked={!!row.asignado}
                                    onChange={(e) => handleToggleZona(row, e.checked)}
                                />
                            </div>
                        )}
                        style={{ width: '4rem' }}
                    />
                    <Column field="codigo" header="Código" sortable />
                    <Column field="descripcion" header="Descripción" sortable />
                </DataTable>
            </>
        </Dialog>
    );
};

export default ZonasDialog;
